import { riskLevel, scheduleStatus } from "../constants/labels";

const SCHEDULE_PRIORITY = ["CHANGE_REVIEW", "CHECK_REQUIRED", "PLAN_REVIEW", "HOLD", "WAITING"];
const RISK_PRIORITY = ["OVERDUE", "HIGH", "ATTENTION"];

const countBy = (items, key) =>
  items.reduce((acc, item) => {
    const value = item?.[key] || "";
    if (!value) return acc;
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});

export const buildOrderStatusSummaryMap = (rows = []) => {
  const grouped = rows.reduce((acc, row) => {
    const key = String(row?.order_id || row?.order_no || row?.order_line_id || "");
    if (!key) return acc;
    acc[key] = acc[key] || [];
    acc[key].push(row);
    return acc;
  }, {});

  return new Map(
    Object.entries(grouped).map(([key, items]) => {
      const scheduleCounts = countBy(items, "schedule_status");
      const riskCounts = countBy(items, "risk_level");
      const scheduleEntries = SCHEDULE_PRIORITY
        .filter((status) => scheduleCounts[status])
        .map((status) => ({
          key: `schedule-${status}`,
          label: `${scheduleStatus[status]?.label || status} ${scheduleCounts[status]}`,
          tone: scheduleStatus[status]?.tone || "#64748b",
          bg: scheduleStatus[status]?.bg || "#f8fafc",
        }));
      const riskEntries = RISK_PRIORITY
        .filter((risk) => riskCounts[risk])
        .map((risk) => ({
          key: `risk-${risk}`,
          label: `${riskLevel[risk]?.label || risk} ${riskCounts[risk]}`,
          tone: riskLevel[risk]?.tone || "#64748b",
          bg: riskLevel[risk]?.bg || "#f8fafc",
        }));

      return [
        key,
        {
          lineCount: items.length,
          entries: [...scheduleEntries, ...riskEntries],
        },
      ];
    })
  );
};

export const buildOrderRowSummary = (row = {}) => {
  const lineCount = Number(row.line_count || 1);
  const scheduleEntries = [
    ["CHANGE_REVIEW", row.schedule_change_review_count],
    ["CHECK_REQUIRED", row.schedule_check_required_count],
    ["PLAN_REVIEW", row.schedule_plan_review_count],
    ["HOLD", row.schedule_hold_count],
    ["WAITING", row.schedule_waiting_count],
  ]
    .filter(([, count]) => Number(count) > 0)
    .map(([status, count]) => ({
      key: `schedule-${status}`,
      label: `${scheduleStatus[status]?.label || status} ${Number(count)}`,
      tone: scheduleStatus[status]?.tone || "#64748b",
      bg: scheduleStatus[status]?.bg || "#f8fafc",
    }));
  const riskEntries = [
    ["OVERDUE", row.risk_overdue_count],
    ["HIGH", row.risk_high_count],
    ["ATTENTION", row.risk_attention_count],
  ]
    .filter(([, count]) => Number(count) > 0)
    .map(([risk, count]) => ({
      key: `risk-${risk}`,
      label: `${riskLevel[risk]?.label || risk} ${Number(count)}`,
      tone: riskLevel[risk]?.tone || "#64748b",
      bg: riskLevel[risk]?.bg || "#f8fafc",
    }));

  return {
    lineCount,
    entries: [...scheduleEntries, ...riskEntries],
  };
};

export const getOrderItemSummaryText = (row = {}) => {
  const itemName = row.item_name_snapshot || "-";
  const lineCount = Number(row.line_count || 1);
  return lineCount > 1 ? `${itemName} 외 ${lineCount - 1}개` : itemName;
};

export const getOrderQuantitySummaryText = (row = {}) => {
  const lineCount = Number(row.line_count || 1);
  const unitCode = row.unit_code || "";
  const quantity = Number(row.order_quantity);

  if (unitCode && unitCode !== "MIXED" && Number.isFinite(quantity)) {
    return `${quantity.toLocaleString()} ${unitCode}`;
  }
  return `품목 ${lineCount}개`;
};
